import { cloneDeep, merge } from "es-toolkit"
import type { Weapon } from "~/types/unit"
import { defaultWeapon } from "~/data/defaults/weapon"

/**
 * Merges incoming weapon with a default weapon, adding in any missing properties and setting their values.
 * 
 * This function performs a deep merge operation, combining the provided partial weapon data
 * with default weapon values. The original weapon object is not mutated - a new object is returned.
 * Properties from the input weapon will override corresponding default properties.
 *
 * @param weapon - Partial weapon data to merge with defaults.
 *                 Partial<Weapon> makes all Weapon properties optional, allowing callers
 *                 to pass incomplete weapon data that gets merged with defaults.
 * @returns Complete weapon object with all required properties. Returns a new object,
 *          does not mutate the input.
 * 
 * @example
 * ```typescript
 * const partialWeapon = { name: "Bolt Rifle", attacks: 2 };
 * const completeWeapon = mergeDefaultsInToWeapon(partialWeapon);
 * // Returns weapon with name "Bolt Rifle", attacks 2, and all other default values
 * ```
 */
function mergeDefaultsInToWeapon(weapon: Partial<Weapon>): Weapon {
  return merge(cloneDeep(defaultWeapon), weapon)
}

export { mergeDefaultsInToWeapon }
